import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import { ACCESS_TOKEN_KEY, APP_TOOLTIP, CONNECT_LOCATION, DASHBOARD_FILTER, HOME_URL_KEY, IMAGE_TOKEN_KEY, REFRESH_TOKEN_KEY, SELECTED_ERP_TYPE, SELECTED_ORGANIZATION, USER_INFO } from '../shared/constants';
import { ApiService } from './api.service';
import { LoggedUserService } from './logged-user.service';
import { LoggedUserModel } from '../models';
import { AppStorage } from 'app/utilities/storage';
import { DateFilterModel } from 'app/models/shared/date-filter.model';
import { AppPageEnum, DateFilterName, TooltipNameEnum } from 'app/shared/enums';
import { ErpType } from 'app/shared/enums';
import { TooltipModel } from 'app/models/shared/tooltip.model';
import { WidgetService } from '../shared/services/widget.service';
import { KindeAuthService } from './kinde-auth.service';
import { KindeUser } from '@kinde-oss/kinde-auth-pkce-js';

@Injectable({ providedIn: 'root' })
export class UserService {

  private _isAuthenticated = false;

  constructor(private router: Router,
              private apiService: ApiService,
              private loggedUserService: LoggedUserService,
              private widgetService: WidgetService,
              private kindeAuth: KindeAuthService) {
  }

  get token(): string {
    return this.apiService.accessToken;
  }

  get refreshToken(): string {
    return this.apiService.refreshToken;
  }

  get currentUser(): Observable<LoggedUserModel | null> {
    return this.loggedUserService.currentUser;
  }

  get loggedUser(): LoggedUserModel | null {
    return this.loggedUserService.loggedUser;
  }

  removeTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(IMAGE_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(HOME_URL_KEY);
    // Also clear marketing-site tokens so a stale sz_access_token doesn't
    // cause issues if the user revisits from the marketing site dashboard.
    localStorage.removeItem('sz_access_token');
    localStorage.removeItem('sz_refresh_token');
    // Clear encryption keys and cached tokens
    AppStorage.clearSecureData();
  }

  saveLocalStorage(key: string, value: string) {
    localStorage.setItem(key, value);
  }
  
  getLocalStorage(key: string) {
    return localStorage.getItem(key) ?? null;
  }

  isLoggedIn(): boolean {
    return this._isAuthenticated;
  }

  setCurrentUserInfo(user: LoggedUserModel) {
    AppStorage.storeLocalStorageData(USER_INFO, user);
    this.loggedUserService.setLoggedUser(user);
    this._isAuthenticated = true;
  }

  getCurrentUserInfo(): LoggedUserModel | null {
    const data = AppStorage.getLocalStorageData(USER_INFO);
    
    if (data) {
      return new LoggedUserModel(data.company_id, data.user_email, data.user_name || data.user_email?.split('@')[0] || '', data.full_name || data.user_name || data.user_email?.split('@')[0] || '', data.user_role);
    }

    return null;
  }

  setTooltips(data: TooltipModel[]) {
    AppStorage.storeLocalStorageDataDefault(APP_TOOLTIP, data);
  }

  setTooltip(data: TooltipModel) {
    const tooltips = this.getTooltips();
    
    delete data.elementTarget;
    delete data.target;

    const updatedData = [
      ...tooltips.filter(_ => _.id !== data.id),
      data
    ];

    AppStorage.storeLocalStorageDataDefault(APP_TOOLTIP, updatedData);
  }

  getTooltips(): Array<TooltipModel> {
    const data = AppStorage.getLocalStorageDataDefault(APP_TOOLTIP);

    if (data) {
      // skipcq: JS-0323
      return data.map((_: any) => new TooltipModel(null, _));
    }

    return new Array<TooltipModel>;
  }

  getTooltip(page: AppPageEnum, tooltipName: TooltipNameEnum | string) {
    return this.getTooltips().firstOrDefault(_ => _.page === page && _.name === tooltipName);
  }

  saveDashboardFilter(data: DateFilterModel) {
    AppStorage.storeLocalStorageData(DASHBOARD_FILTER, data);
  }

  getDashboardFilter() {
    const data = AppStorage.getLocalStorageData(DASHBOARD_FILTER);
    
    if (data) {
      return new DateFilterModel(data);
    } 
  
    let startDate = new Date();
    const endDate = new Date();
    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    return new DateFilterModel({
      filterType: 'month',
      name: DateFilterName.Month,
      startDate,
      endDate
    });
  }

  // skipcq: JS-0105
  saveConnectLocation(location: 'LIST' | 'DETAIL') {
    AppStorage.storeLocalStorageData(CONNECT_LOCATION, location);
  }

  // skipcq: JS-0105
  getConnectLocation() {
    return AppStorage.getLocalStorageData(CONNECT_LOCATION) as 'LIST' | 'DETAIL';
  }

  saveSelectedErpType(value: string) {
    AppStorage.storeLocalStorageData(SELECTED_ERP_TYPE, value);
  }

  saveSelectedOrganization(erpType: ErpType, value: string) {
    const selectedOrg = `{"${erpType}": "${value}"}`;
    AppStorage.storeLocalStorageData(SELECTED_ORGANIZATION, JSON.parse(selectedOrg));
  }

  getSelectedErpType() {
    return AppStorage.getLocalStorageData(SELECTED_ERP_TYPE) as ErpType;
  }

  getSelectedOrganization(erpType: ErpType) {
    // skipcq: JS-0323
    const selectedOrg = AppStorage.getLocalStorageData(SELECTED_ORGANIZATION) as any;

    return selectedOrg ? selectedOrg[`${erpType}`]: undefined;
  }

  redirectToHome(homeUrl?: string) {
    if (!homeUrl) {
      homeUrl = AppStorage.getTokenData(HOME_URL_KEY);
    }
    if (homeUrl) {
      this.router.navigateByUrl(homeUrl);
    } else {
      console.error('None homeUrl');
      this.router.navigate(['/']).then();
    }
  }

  removeCurrentUser() {
    //
    this.loggedUserService.setLoggedUser(null);
    //
    this.removeTokens();
  }

  /**
   * Populates the logged-in user state from a Kinde user profile.
   *
   * This is called after Kinde authentication succeeds. The backend will
   * resolve the company_id from the email claim in the Kinde access token
   * on the first authenticated API request — we store 0 as a placeholder.
   */
  setCurrentUserFromKinde(kindeUser: KindeUser): void {
    const givenName = kindeUser.given_name ?? '';
    const familyName = kindeUser.family_name ?? '';
    const fullName = [givenName, familyName].filter(Boolean).join(' ')
      || kindeUser.email?.split('@')[0]
      || '';
    const userName = givenName || kindeUser.email?.split('@')[0] || '';

    this.setCurrentUserInfo(
      new LoggedUserModel(0, kindeUser.email ?? '', userName, fullName, undefined)
    );
  }

  logout() {
    this.removeCurrentUser();
    this._isAuthenticated = false;
    this.kindeAuth.logout();
  }

  navigateToLogin() {
    this.removeCurrentUser();
    this._isAuthenticated = false;
    this.kindeAuth.login();
  }

  changeUser(value: string, emailToSend: string) {
    this.apiService.changeUser(value , emailToSend);
  }

  getSignInUser() {
    return this.apiService.signInUser;
  }
}
